import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import CredentialSheet from '../chat/sidebar/CredentialSheet';
import SecureInputStage from '../chat/sidebar/SecureInputStage';
import '../chat/sidebar/secure-input-sheet.scss';
import '../chat/sidebar/payment-challenge-modal.scss';

/**
 * Ocado in Settings → Connected Apps. Connect opens Ocado's own log-in page,
 * live in a hosted browser; the owner logs in there themselves (password and
 * Ocado's emailed code). On a computer they type straight into it. A phone
 * can't raise its keyboard for the live view, so there it opens at phone size
 * and a text box of ours forwards each keystroke, never stored or logged.
 * Dream watches for "signed in", then keeps the login for their shared shops.
 *
 * Talks to the JWT-only `/shops/ocado` routes; a 404 there means shops are
 * off for this user and the app is not shown. The live-view URL is held in
 * component state only — never persisted, logged or rendered as text.
 */

export type OcadoState = 'none' | 'connected' | 'logged_out';

export interface OcadoConnection {
  state: OcadoState;
  shops: { name: string; members: string[] }[];
}

const POLL_MS = 3000;

/** The phone's screen, or null on a computer (where typing works directly). */
export const phoneScreen = (): { width: number; height: number } | null => {
  if (typeof window === 'undefined' || !window.matchMedia) return null;
  if (!window.matchMedia('(pointer: coarse)').matches) return null;
  if (window.innerWidth > 600) return null;
  return {
    width: Math.round(Math.min(window.innerWidth, 480) - 32),
    height: Math.round(window.innerHeight * 0.6),
  };
};

export type TypeInput =
  | { text: string }
  | { key: 'Backspace' | 'Enter' | 'Tab' };

/**
 * The phone's own keyboard, forwarded to the focused box on Ocado's page.
 * The box is always emptied straight away, so nothing typed stays on screen.
 */
export const PhoneKeyboard: React.FC<{
  send: (input: TypeInput) => Promise<void>;
  appName: string;
}> = ({ send, appName }) => {
  const queue = useRef<Promise<void>>(Promise.resolve());
  const [failed, setFailed] = useState(false);
  const push = useCallback(
    (input: TypeInput) => {
      queue.current = queue.current
        .then(() => send(input))
        .then(
          () => setFailed(false),
          () => setFailed(true),
        );
    },
    [send],
  );
  return (
    <div className='phone-keys'>
      <input
        type='text'
        value=''
        aria-label={`Type into ${appName}`}
        placeholder='Tap a box above, then type here'
        autoComplete='off'
        autoCapitalize='none'
        autoCorrect='off'
        spellCheck={false}
        enterKeyHint='go'
        data-sentry-mask='true'
        className='ph-no-capture'
        onChange={(e) => {
          if (e.target.value) push({ text: e.target.value });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Backspace') {
            e.preventDefault();
            push({ key: 'Backspace' });
          } else if (e.key === 'Enter') {
            e.preventDefault();
            push({ key: 'Enter' });
          }
        }}
      />
      <button
        type='button'
        className='tx-btn-text'
        onClick={() => push({ key: 'Tab' })}
      >
        Next box
      </button>
      {failed && (
        <p className='phone-keys-error'>
          That didn&rsquo;t reach Ocado. Try typing it again.
        </p>
      )}
    </div>
  );
};

export const OcadoLogo: React.FC<{ inverted?: boolean }> = ({ inverted }) => (
  <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true'>
    <circle cx='12' cy='12' r='11' fill={inverted ? '#fff' : '#4f1f6b'} />
    <path
      d='M12 6.5a5.5 5.5 0 1 0 5.2 7.3'
      stroke={inverted ? '#4f1f6b' : '#fff'}
      strokeWidth='2.4'
      fill='none'
      strokeLinecap='round'
    />
    <circle cx='12.6' cy='11.4' r='1.9' fill={inverted ? '#4f1f6b' : '#fff'} />
  </svg>
);

const statusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const response = (error as { response?: { status?: unknown } }).response;
  return typeof response?.status === 'number' ? response.status : undefined;
};

/** null until loaded, and stays null when shops are off for this user. */
export function useOcadoConnection(): {
  connection: OcadoConnection | null;
  refresh: () => Promise<void>;
} {
  const { getAccessTokenSilently } = useAuth0();
  const [connection, setConnection] = useState<OcadoConnection | null>(null);
  const refresh = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.get<OcadoConnection>('/shops/ocado', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConnection(data);
    } catch (error) {
      if (statusOf(error) !== 404) {
        console.error('Error loading Ocado connection');
      }
      setConnection(null);
    }
  }, [getAccessTokenSilently]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return { connection, refresh };
}

type Phase = 'opening' | 'live' | 'connected' | 'expired' | 'busy' | 'error';

const OcadoConnectSheet: React.FC<{
  assistantName: string;
  onClose: (connected: boolean) => void;
}> = ({ assistantName, onClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [phase, setPhase] = useState<Phase>('opening');
  const [liveViewUrl, setLiveViewUrl] = useState<string | null>(null);
  const phaseRef = useRef<Phase>('opening');
  phaseRef.current = phase;
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [phone] = useState(phoneScreen);

  const auth = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post<{ liveViewUrl: string }>(
          '/shops/ocado/connect',
          phone ? { screen: phone } : {},
          await auth(),
        );
        if (cancelled) return;
        setLiveViewUrl(data.liveViewUrl);
        setPhase('live');
      } catch (error) {
        if (!cancelled) setPhase(statusOf(error) === 409 ? 'busy' : 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, phone]);

  const typeIntoOcado = useCallback(
    async (input: TypeInput) => {
      await api.post('/shops/ocado/connect/type', input, await auth());
    },
    [auth],
  );

  useEffect(() => {
    if (phase !== 'live') return undefined;
    let stopped = false;
    let timer: number | undefined;
    // One check at a time: a slow check must not overlap the next one.
    const poll = async () => {
      try {
        const { data } = await api.get<{ state: string }>(
          '/shops/ocado/connect',
          await auth(),
        );
        if (stopped || phaseRef.current !== 'live') return;
        if (data.state === 'connected') {
          setLiveViewUrl(null);
          setPhase('connected');
          return;
        }
        if (data.state === 'expired') {
          setLiveViewUrl(null);
          setPhase('expired');
          return;
        }
      } catch {
        // Keep polling; a blip shouldn't close their log-in.
      }
      if (!stopped) timer = window.setTimeout(poll, POLL_MS);
    };
    timer = window.setTimeout(poll, POLL_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [phase, auth]);

  // Tab out of the live view lands on this page, next to Cancel, so Enter
  // could close the log-in. Send focus back to the frame instead; they click
  // the next Ocado box.
  const keepFocusInFrame = useCallback(() => frameRef.current?.focus(), []);

  const close = useCallback(async () => {
    const connected = phaseRef.current === 'connected';
    if (!connected) {
      try {
        await api.post('/shops/ocado/connect/cancel', {}, await auth());
      } catch {
        // The log-in browser times out by itself.
      }
    }
    setLiveViewUrl(null);
    onClose(connected);
  }, [auth, onClose]);

  const footer =
    phase === 'live' ? (
      <button type='button' className='tx-btn-text' onClick={close}>
        Cancel
      </button>
    ) : phase === 'opening' ? undefined : (
      <button type='button' className='tx-btn-primary' onClick={close}>
        {phase === 'connected' ? 'Done' : 'Close'}
      </button>
    );

  return (
    <CredentialSheet
      show
      onClose={close}
      className={`secure-input-sheet payment-challenge-modal ocado-connect-sheet${
        phase === 'live' ? ' live' : ''
      }`}
      ariaLabelledBy='ocado-connect-title'
      holdOpen={phase === 'opening' || phase === 'live'}
      footer={footer}
    >
      {phase === 'opening' && (
        <SecureInputStage
          kind='signing-in'
          title='Opening Ocado…'
          titleId='ocado-connect-title'
          busy
        />
      )}
      {phase === 'live' && (
        <>
          <div className='tx-head'>
            <h2 className='tx-title hero' id='ocado-connect-title'>
              Log in <span className='accent'>to Ocado</span>
            </h2>
            <p className='brief'>
              {phone ? (
                <>
                  This is Ocado&rsquo;s own page. Tap a box on it, then type
                  below. {assistantName} passes what you type to Ocado and never
                  keeps it.
                </>
              ) : (
                <>
                  This is Ocado&rsquo;s own page. {assistantName} never sees
                  your password or code.
                </>
              )}
            </p>
          </div>
          {liveViewUrl && (
            <div className='live-view'>
              <span
                tabIndex={0}
                aria-hidden='true'
                className='tab-catch'
                data-testid='ocado-tab-before'
                onFocus={keepFocusInFrame}
              />
              <iframe
                ref={frameRef}
                title='Ocado log in'
                src={liveViewUrl}
                sandbox='allow-same-origin allow-scripts allow-forms allow-modals'
                allow='clipboard-read; clipboard-write'
                referrerPolicy='no-referrer'
                data-sensitive='true'
                data-sentry-mask='true'
                className='ph-no-capture'
              />
              <span
                tabIndex={0}
                aria-hidden='true'
                className='tab-catch'
                data-testid='ocado-tab-after'
                onFocus={keepFocusInFrame}
              />
            </div>
          )}
          {liveViewUrl && phone && (
            <PhoneKeyboard appName='Ocado' send={typeIntoOcado} />
          )}
        </>
      )}
      {phase === 'connected' && (
        <SecureInputStage
          kind='signed-in'
          title='Ocado connected'
          titleId='ocado-connect-title'
        >
          <p>{assistantName} can fill your basket now.</p>
        </SecureInputStage>
      )}
      {phase === 'expired' && (
        <SecureInputStage
          kind='expired'
          title='The Ocado page timed out'
          titleId='ocado-connect-title'
        >
          <p>Nothing was saved. Press Connect to try again.</p>
        </SecureInputStage>
      )}
      {phase === 'busy' && (
        <SecureInputStage
          kind='error'
          title='Ocado is busy'
          titleId='ocado-connect-title'
        >
          <p>
            {assistantName} is using your browser right now. Try again in a
            minute.
          </p>
        </SecureInputStage>
      )}
      {phase === 'error' && (
        <SecureInputStage
          kind='error'
          title="Couldn't open Ocado"
          titleId='ocado-connect-title'
        >
          <p>Please try again in a minute.</p>
        </SecureInputStage>
      )}
    </CredentialSheet>
  );
};

const joinNames = (names: string[]): string =>
  names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export const OcadoAppView: React.FC<{
  connection: OcadoConnection;
  header: React.ReactNode;
  assistantName: string;
  autoConnect: boolean;
  onChanged: () => Promise<void>;
}> = ({ connection, header, assistantName, autoConnect, onChanged }) => {
  const [connecting, setConnecting] = useState(autoConnect);
  const [justConnected, setJustConnected] = useState(false);

  const done = useCallback(
    async (connected: boolean) => {
      setConnecting(false);
      if (connected) setJustConnected(true);
      await onChanged();
    },
    [onChanged],
  );

  const shopsLine =
    connection.shops.length > 0 ? (
      <p className='ca-connect-hint ocado-used-by'>
        Used by{' '}
        {connection.shops.map((s, i) => (
          <React.Fragment key={s.name + i}>
            {i > 0 && ', '}
            <strong>{s.name}</strong>
            {s.members.length > 0 && ` (you and ${joinNames(s.members)})`}
          </React.Fragment>
        ))}
        .
      </p>
    ) : null;

  return (
    <>
      {header}
      {justConnected && connection.state === 'connected' && (
        <p className='connected-accounts-feedback success'>Ocado connected</p>
      )}
      <div className='ca-detail-card'>
        {connection.state === 'none' ? (
          <div className='ca-connect-row'>
            <button
              className='ca-connect-btn'
              onClick={() => setConnecting(true)}
            >
              <OcadoLogo inverted />
              <span>Connect my Ocado</span>
            </button>
            <p className='ca-connect-hint'>
              {assistantName} fills your Ocado basket. You log in on
              Ocado&rsquo;s own page, and you still book the slot and pay.
            </p>
          </div>
        ) : (
          <div className='ca-account-list'>
            <div className='ca-account-row'>
              <div className='ca-account-info'>
                <span className='ca-account-icon'>
                  <OcadoLogo />
                </span>
                <span className='ca-account-label'>Ocado</span>
                {connection.state === 'logged_out' ? (
                  <span className='ca-account-limited'>Logged out</span>
                ) : (
                  <span className='ca-app-status'>Connected</span>
                )}
              </div>
            </div>
            {connection.state === 'logged_out' && (
              <button
                className='ca-connect-btn'
                onClick={() => setConnecting(true)}
              >
                <OcadoLogo inverted />
                <span>Reconnect Ocado</span>
              </button>
            )}
          </div>
        )}
        {shopsLine}
      </div>
      {connecting && (
        <OcadoConnectSheet assistantName={assistantName} onClose={done} />
      )}
    </>
  );
};
